import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  CircularProgress, 
  Alert, 
  Card, 
  CardMedia, 
  Button,
  Divider,
  Breadcrumbs,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import dayjs from 'dayjs';
import AdvertComponent from '../components/AdvertComponent';

// Define types for WordPress post data
interface WordPressImage {
  id: number;
  source_url: string;
  alt_text: string;
}

interface WordPressPost {
  id: number;
  date: string;
  title: { rendered: string };
  content: { rendered: string };
  excerpt: { rendered: string };
  _embedded?: {
    'wp:featuredmedia'?: WordPressImage[];
    'author'?: Array<{
      name: string;
    }>;
  };
}

const ArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<WordPressPost | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [adInserted, setAdInserted] = useState(false);

  const WORDPRESS_API_URL = 'https://xyz.xaujournal.com/wp-json/wp/v2/posts';

  useEffect(() => {
    const fetchPost = async () => {
      if (!id) {
        setError('Article ID is missing');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${WORDPRESS_API_URL}/${id}?_embed`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setPost(data);
      } catch (err: any) {
        console.error("Failed to fetch article:", err);
        setError(err.message || 'Failed to load the article. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  // Insert advertisement after the article content is loaded
  useEffect(() => {
    if (!loading && post && contentRef.current && !adInserted) {
      // Find paragraphs in the content
      const paragraphs = contentRef.current.querySelectorAll('p');
      
      // Only insert ad if we have at least one paragraph
      if (paragraphs.length > 0) {
        // Get the first paragraph
        const firstPara = paragraphs[0];
        
        // Create a wrapper div for the advertisement
        const adContainer = document.createElement('div');
        adContainer.id = 'article-ad-container';
        
        // Insert the wrapper after the first paragraph
        if (firstPara.nextSibling) {
          firstPara.parentNode?.insertBefore(adContainer, firstPara.nextSibling);
        } else {
          firstPara.parentNode?.appendChild(adContainer);
        }
        
        // Use ReactDOM to render the AdvertComponent into the wrapper
        try {
          // For React 18+
          const { createRoot } = require('react-dom/client');
          const root = createRoot(adContainer);
          root.render(<AdvertComponent />);
          setAdInserted(true);
        } catch (error) {
          // Fallback for older React versions
          const ReactDOM = require('react-dom');
          ReactDOM.render(<AdvertComponent />, adContainer);
          setAdInserted(true);
        }
      }
    }
  }, [loading, post, adInserted]);

  // Format the post date
  const formatPostDate = (dateStr: string) => {
    return dayjs(dateStr).format('MMMM D, YYYY');
  };

  // Get featured image URL
  const getFeaturedImageUrl = (post: WordPressPost) => {
    if (post._embedded && 
        post._embedded['wp:featuredmedia'] && 
        post._embedded['wp:featuredmedia'][0] &&
        post._embedded['wp:featuredmedia'][0].source_url) {
      return post._embedded['wp:featuredmedia'][0].source_url;
    }
    
    return `${process.env.PUBLIC_URL}/assets/images/goldbar.png`;
  };

  // Get author name
  const getAuthorName = (post: WordPressPost) => {
    if (post._embedded && post._embedded.author && post._embedded.author[0]) {
      return post._embedded.author[0].name;
    }
    return 'XAU Journal Staff';
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        // Remove horizontal padding on xs and sm screens
        // Relies on the parent .main-content class from Layout.css for page padding on mobile
        paddingLeft: { xs: 0, sm: 0 },
        paddingRight: { xs: 0, sm: 0 },
      }}
    >
      <Box sx={{ my: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            Home
          </Link>
          <Link color="inherit" onClick={() => navigate('/news')} sx={{ cursor: 'pointer' }}>
            News
          </Link>
          <Typography color="text.primary">
            {loading ? 'Loading...' : post?.title.rendered ? post.title.rendered.replace(/&#8230;/g, '...') : 'Article'}
          </Typography>
        </Breadcrumbs>
        
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/news')}
          sx={{ mb: 3 }}
        >
          Back to News
        </Button>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && post && (
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              dangerouslySetInnerHTML={{ __html: post.title.rendered }}
              sx={{ fontWeight: 'bold', mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                By {getAuthorName(post)}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {formatPostDate(post.date)}
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Card 
              elevation={0}
              sx={{ 
                mb: 4,
                bgcolor: 'transparent', 
              }}
            >
              <CardMedia
                component="img"
                height="450"
                image={getFeaturedImageUrl(post)}
                alt={post.title.rendered}
                sx={{ 
                  objectFit: 'cover',
                  borderRadius: 1,
                  mb: 3
                }}
              />
              
              <Box 
                ref={contentRef}
                className="article-content" 
                sx={{
                  '& img': { maxWidth: '100%', height: 'auto' },
                  '& a': { color: 'primary.main' },
                  '& h2': { fontSize: '1.5rem', fontWeight: 'bold', mt: 4, mb: 2 },
                  '& h3': { fontSize: '1.3rem', fontWeight: 'bold', mt: 3, mb: 2 },
                  '& p': { 
                    mb: 2, 
                    fontSize: '1.1rem', 
                    lineHeight: 1.7,
                    textAlign: 'justify', // Add text justification
                    hyphens: 'auto',
                    WebkitHyphens: 'auto',
                    msHyphens: 'auto',
                    overflowWrap: 'break-word',
                    wordWrap: 'break-word'
                  },
                  '& ul, & ol': { ml: 4, mb: 3 },
                }}
                dangerouslySetInnerHTML={{ __html: post.content.rendered }} 
              />
            </Card>
            
            <Box sx={{ mt: 4 }}>
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={() => navigate('/news')}
                variant="outlined"
              >
                Back to All Articles
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ArticlePage;